// InboxPage — /inbox
//
// Phase 3 of Leads Inbox: the list view.
//
// Two tabs (Direct / Pool) with unread counts in their labels. Filter chips
// for status, free-text search across name/email/note, dense list rows with
// thumbnail + status pill + unread dot.
//
// Detail navigation goes to /inbox/lead/:id and /inbox/pool/:id, which are
// wired in Phase 4. Until then, clicking a row will fall through to the
// router's default not-found — that's expected and we'll fix it next phase.
//
// Entitlement gate: this page checks isEntitled("leads") and shows a
// non-scary "Artist Pro required" pane if the user isn't entitled. Phase 5
// will hide the nav entry entirely for those users; this in-page gate is the
// belt-and-suspenders fallback for direct-URL navigation.
//
// Styling notes:
//   - Mirrors BillingPage's PageShell pattern (dark #0a0a0a, max-width
//     container, inline styles).
//   - Bumped max-width to 820 to give list rows breathing room.
//   - Reuses the `tt-billing-btn-primary` workaround for the upgrade CTA so
//     the global !important button rule doesn't clobber the background.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useLeadsInbox } from "../hooks/useLeadsInbox";
import { useSubscription } from "../hooks/useSubscription";

// --- Workaround classes for our themed buttons.
// --- The actual rule we're guarding against is `button { background: #111827 }`
// --- in src/styles.css, plus `.tattoo-app button { ... }` in tattoo-ui.css
// --- (scoped to the editor, doesn't reach this page). Neither uses !important.
// --- Class specificity (0,0,1,0) already beats element specificity (0,0,0,1),
// --- so the !important markers below are technically redundant — kept for
// --- defensive parity with BillingPage / LeadDetail / PoolDetail / Booking
// --- until we do a visual sweep across all themed pages.
const inboxButtonStyles = `
.tt-inbox-btn-primary {
  background: #a2332b !important;
  color: #ffffff !important;
}
.tt-inbox-btn-primary:hover:not(:disabled) {
  background: #c2403a !important;
}
.tt-inbox-btn-ghost {
  background: transparent !important;
  color: #d7d7d7 !important;
}
.tt-inbox-btn-ghost:hover:not(:disabled) {
  background: #1a1a1a !important;
}
.tt-inbox-row {
  background: transparent !important;
  color: inherit !important;
}
.tt-inbox-row:hover {
  background: #1a1a1a !important;
}
.tt-inbox-chip {
  background: transparent !important;
  color: #cfcfcf !important;
}
.tt-inbox-chip[data-active="true"] {
  background: #1f1f1f !important;
  color: #ffffff !important;
}
.tt-inbox-chip:hover:not([data-active="true"]) {
  background: #161616 !important;
}
.tt-inbox-tab {
  background: transparent !important;
  color: #a8a8a8 !important;
}
.tt-inbox-tab[data-active="true"] {
  background: #1f1f1f !important;
  color: #ffffff !important;
}
.tt-inbox-tab:hover:not([data-active="true"]) {
  background: #161616 !important;
}
@keyframes tt-inbox-skeleton-pulse {
  0%   { opacity: 0.6; }
  50%  { opacity: 1;   }
  100% { opacity: 0.6; }
}
.tt-inbox-skeleton {
  animation: tt-inbox-skeleton-pulse 1.4s ease-in-out infinite;
}
`;

// Status options shown as filter chips. "All" is implicit (null filter).
// "Unread" is computed differently per source (see filteredRows).
const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "contacted", label: "Contacted" },
  { value: "booked", label: "Booked" },
  { value: "passed", label: "Passed" },
  { value: "archived", label: "Archived" },
];

// Sort options shared across both tabs. "Unread first" sub-sorts by newest
// inside the unread group so the order is deterministic.
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "unread", label: "Unread first" },
];

// Status pill colors. Pool requests use a smaller subset (no "new"/"booked").
const STATUS_PILL = {
  new: { label: "New", bg: "#a2332b", fg: "#fff" },
  viewed: { label: "Viewed", bg: "#2a2a2a", fg: "#cfcfcf" },
  contacted: { label: "Contacted", bg: "#1d4ed8", fg: "#fff" },
  booked: { label: "Booked", bg: "#10b981", fg: "#0a0a0a" },
  passed: { label: "Passed", bg: "#3a3a3a", fg: "#a8a8a8" },
  archived: { label: "Archived", bg: "#3a3a3a", fg: "#a8a8a8" },
  responded: { label: "Responded", bg: "#1d4ed8", fg: "#fff" },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function InboxPage() {
  const navigate = useNavigate();
  const sub = useSubscription();
  const inbox = useLeadsInbox();

  const [tab, setTab] = useState("direct"); // "direct" | "pool"
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [markingAll, setMarkingAll] = useState(false);

  // ---- entitlement gate ---------------------------------------------------
  // Render a friendly upgrade pane instead of an error if the user lands
  // here without entitlement. Phase 5 will also hide the nav entry.
  if (!sub.loading && !sub.isEntitled("leads")) {
    return (
      <PageShell onBack={() => navigate("/app")}>
        <ArtistGate
          onUpgrade={() => navigate("/account/billing")}
        />
      </PageShell>
    );
  }

  // ---- loading ------------------------------------------------------------
  // Render the same scaffolding as the loaded view (tabs + filter chips +
  // search) but with placeholder rows, so the layout doesn't shift when
  // data arrives.
  if (sub.loading || inbox.loading) {
    return (
      <PageShell onBack={() => navigate("/app")}>
        <SkeletonScaffold />
      </PageShell>
    );
  }

  // ---- "no shop yet" ------------------------------------------------------
  // Hook returns shopId=null when the user is entitled (artist trial active)
  // but hasn't set up their shop yet. Friendly pointer back to the wizard.
  if (!inbox.shopId) {
    return (
      <PageShell onBack={() => navigate("/app")}>
        <NoShopState onGoToWizard={() => navigate("/app")} />
      </PageShell>
    );
  }

  // ---- inbox error --------------------------------------------------------
  if (inbox.error) {
    return (
      <PageShell onBack={() => navigate("/app")}>
        <div
          style={{
            background: "#1a0f0f",
            border: "1px solid #5a1a14",
            borderRadius: 12,
            padding: "16px 18px",
            color: "#f6caca",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          Couldn't load your inbox: {inbox.error}
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="tt-inbox-btn-ghost"
              onClick={() => inbox.refresh()}
              style={ghostButtonStyle}
            >
              Try again
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  // ---- main view ----------------------------------------------------------

  function handleRowClick(kind, id) {
    // Phase 4 wires these. For now this navigates to a 404 — leave the
    // navigate() call in place so Phase 4 only needs to add the routes.
    if (kind === "direct") {
      navigate(`/inbox/lead/${id}`);
    } else {
      navigate(`/inbox/pool/${id}`);
    }
  }

  // Mark-all-as-read for the currently active tab.
  // Disabled while a request is in flight to prevent double-clicks
  // (the optimistic UI clears the unread count instantly so the button
  // would otherwise still be clickable for a frame after success).
  async function handleMarkAllRead() {
    if (markingAll) return;
    setMarkingAll(true);
    try {
      const count =
        tab === "direct"
          ? await inbox.markAllDirectViewed()
          : await inbox.markAllPoolViewed();
      if (count > 0) {
        toast.success(
          count === 1
            ? "Marked 1 as read"
            : `Marked ${count} as read`
        );
      }
    } catch (e) {
      console.warn("[inbox] mark-all-as-read failed", e);
      toast.error("Couldn't mark all as read — try again.");
    } finally {
      setMarkingAll(false);
    }
  }

  const currentTabUnread =
    tab === "direct" ? inbox.unreadDirect : inbox.unreadPool;

  return (
    <PageShell onBack={() => navigate("/app")}>
      {/* Tabs */}
      <div
        role="tablist"
        aria-label="Inbox sources"
        style={{
          display: "flex",
          gap: 6,
          padding: 4,
          background: "#0f0f0f",
          border: "1px solid #1f1f1f",
          borderRadius: 12,
          marginBottom: 18,
        }}
      >
        <TabButton
          active={tab === "direct"}
          onClick={() => setTab("direct")}
          label="Direct"
          unread={inbox.unreadDirect}
        />
        <TabButton
          active={tab === "pool"}
          onClick={() => setTab("pool")}
          label="Pool"
          unread={inbox.unreadPool}
        />
      </div>

      {/* Filter chips */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: 12,
        }}
      >
        {FILTER_OPTIONS.map((opt) => (
          <FilterChip
            key={opt.value}
            label={opt.label}
            active={filter === opt.value}
            onClick={() => setFilter(opt.value)}
          />
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            tab === "direct"
              ? "Search by name, email, or note…"
              : "Search by name, styles, or note…"
          }
          style={{
            width: "100%",
            padding: "10px 14px",
            background: "#0f0f0f",
            border: "1px solid #1f1f1f",
            borderRadius: 10,
            color: "#eaeaea",
            fontSize: 14,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Toolbar: sort + mark-all-as-read */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
          minHeight: 32,
        }}
      >
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#a8a8a8",
          }}
        >
          <span>Sort</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={{
              padding: "6px 28px 6px 10px",
              background: "#0f0f0f",
              border: "1px solid #1f1f1f",
              borderRadius: 8,
              color: "#eaeaea",
              fontSize: 12,
              outline: "none",
              cursor: "pointer",
            }}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        {currentTabUnread > 0 && (
          <button
            type="button"
            className="tt-inbox-btn-ghost"
            onClick={handleMarkAllRead}
            disabled={markingAll}
            style={{
              padding: "6px 12px",
              border: "1px solid #2a2a2a",
              borderRadius: 8,
              fontSize: 12,
              cursor: markingAll ? "default" : "pointer",
              opacity: markingAll ? 0.6 : 1,
            }}
          >
            {markingAll
              ? "Marking…"
              : `Mark all as read (${currentTabUnread})`}
          </button>
        )}
      </div>

      {/* List */}
      {tab === "direct" ? (
        <DirectList
          direct={inbox.direct}
          filter={filter}
          search={search}
          sort={sort}
          shop={inbox.shop}
          onOpen={(id) => handleRowClick("direct", id)}
        />
      ) : (
        <PoolList
          pool={inbox.pool}
          filter={filter}
          search={search}
          sort={sort}
          onOpen={(id) => handleRowClick("pool", id)}
        />
      )}
    </PageShell>
  );
}

// ===========================================================================
// Lists
// ===========================================================================

function DirectList({ direct, filter, search, sort, shop, onOpen }) {
  const rows = useMemo(
    () => sortDirect(filterDirect(direct, filter, search), sort),
    [direct, filter, search, sort]
  );

  if (rows.length === 0) {
    return (
      <DirectEmpty
        hasAnyDirect={direct.length > 0}
        filterActive={filter !== "all" || !!search.trim()}
        shop={shop}
      />
    );
  }

  return (
    <div
      style={{
        background: "#0f0f0f",
        border: "1px solid #1f1f1f",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      {rows.map((lead, idx) => (
        <DirectRow
          key={lead.id}
          lead={lead}
          first={idx === 0}
          onOpen={() => onOpen(lead.id)}
        />
      ))}
    </div>
  );
}

function PoolList({ pool, filter, search, sort, onOpen }) {
  const rows = useMemo(
    () => sortPool(filterPool(pool, filter, search), sort),
    [pool, filter, search, sort]
  );

  if (rows.length === 0) {
    return (
      <PoolEmpty
        hasAnyPool={pool.length > 0}
        filterActive={filter !== "all" || !!search.trim()}
      />
    );
  }

  return (
    <div
      style={{
        background: "#0f0f0f",
        border: "1px solid #1f1f1f",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      {rows.map((entry, idx) => (
        <PoolRow
          key={entry.request.id}
          entry={entry}
          first={idx === 0}
          onOpen={() => onOpen(entry.request.id)}
        />
      ))}
    </div>
  );
}

// ===========================================================================
// Rows
// ===========================================================================

function DirectRow({ lead, first, onOpen }) {
  const isUnread = lead.status === "new";
  const pill = STATUS_PILL[lead.status] || STATUS_PILL.viewed;
  const thumb = lead.design_images?.[0];
  const notePreview = lead.note?.trim() || lead.user_email || "—";

  return (
    <button
      type="button"
      className="tt-inbox-row"
      onClick={onOpen}
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 12,
        padding: "14px 14px 14px 12px",
        width: "100%",
        textAlign: "left",
        border: "none",
        borderTop: first ? "none" : "1px solid #1a1a1a",
        cursor: "pointer",
      }}
    >
      <UnreadDot show={isUnread} />
      <Thumb src={thumb} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: isUnread ? 700 : 500,
              color: "#eaeaea",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {lead.user_name || "Anonymous"}
          </div>
          <StatusPill spec={pill} />
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#a8a8a8",
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lead.user_email || lead.user_phone || "No contact info"}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#888",
            marginTop: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          “{truncate(notePreview, 80)}”
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
          {formatRelative(lead.created_at)}
        </div>
      </div>
    </button>
  );
}

function PoolRow({ entry, first, onOpen }) {
  const { request, view } = entry;
  const isUnread = !view;
  const pillKey = view?.status || "new"; // no view = "new" badge
  const pill =
    pillKey === "new"
      ? { label: "New", bg: "#a2332b", fg: "#fff" }
      : STATUS_PILL[pillKey] || STATUS_PILL.viewed;
  const thumb = request.design_images?.[0];
  const stylesText =
    Array.isArray(request.style_tags) && request.style_tags.length
      ? request.style_tags.join(" · ")
      : null;
  const notePreview =
    request.note?.trim() || stylesText || request.budget_text || "—";

  return (
    <button
      type="button"
      className="tt-inbox-row"
      onClick={onOpen}
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 12,
        padding: "14px 14px 14px 12px",
        width: "100%",
        textAlign: "left",
        border: "none",
        borderTop: first ? "none" : "1px solid #1a1a1a",
        cursor: "pointer",
      }}
    >
      <UnreadDot show={isUnread} />
      <Thumb src={thumb} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: isUnread ? 700 : 500,
              color: "#eaeaea",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {request.user_name || "Anonymous"}
          </div>
          <StatusPill spec={pill} />
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#a8a8a8",
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {[request.user_city, request.user_country]
            .filter(Boolean)
            .join(", ") ||
            request.user_email ||
            "No location"}
          {stylesText && (
            <span style={{ color: "#666" }}> · {stylesText}</span>
          )}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#888",
            marginTop: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          “{truncate(notePreview, 80)}”
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
          {formatRelative(request.created_at)}
          {request.expires_at && (
            <span style={{ color: "#555" }}>
              {" · "}
              {formatExpiry(request.expires_at)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ===========================================================================
// Bits & pieces
// ===========================================================================

function UnreadDot({ show }) {
  return (
    <div
      style={{
        width: 8,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 8,
      }}
    >
      {show && (
        <span
          aria-label="Unread"
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#a2332b",
          }}
        />
      )}
    </div>
  );
}

function Thumb({ src }) {
  return (
    <div
      style={{
        width: 56,
        height: 56,
        flexShrink: 0,
        borderRadius: 8,
        background: "#1a1a1a",
        border: "1px solid #2a2a2a",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <span style={{ fontSize: 18, color: "#444" }}>·</span>
      )}
    </div>
  );
}

function StatusPill({ spec }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        background: spec.bg,
        color: spec.fg,
        whiteSpace: "nowrap",
      }}
    >
      {spec.label}
    </span>
  );
}

function TabButton({ active, onClick, label, unread }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-active={active ? "true" : "false"}
      className="tt-inbox-tab"
      onClick={onClick}
      style={{
        flex: 1,
        padding: "10px 12px",
        border: "none",
        borderRadius: 9,
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
      }}
    >
      <span>{label}</span>
      {unread > 0 && (
        <span
          aria-label={`${unread} unread`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 20,
            height: 20,
            padding: "0 6px",
            borderRadius: 999,
            background: "#a2332b",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      data-active={active ? "true" : "false"}
      className="tt-inbox-chip"
      onClick={onClick}
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        border: "1px solid #1f1f1f",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.02em",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

// ===========================================================================
// Empty / gate views
// ===========================================================================

function DirectEmpty({ hasAnyDirect, filterActive, shop }) {
  if (filterActive) {
    return (
      <Card>
        <h3 style={emptyTitleStyle}>No matches</h3>
        <p style={emptyBodyStyle}>
          No direct leads match the current filter or search. Try clearing
          filters above.
        </p>
      </Card>
    );
  }

  if (hasAnyDirect) {
    // hasAnyDirect=true means there are leads but the filter happened to
    // exclude them all without an explicit filter-active flag — defensive
    // fallback, shouldn't normally fire.
    return (
      <Card>
        <h3 style={emptyTitleStyle}>Nothing to show</h3>
        <p style={emptyBodyStyle}>
          You're all caught up — no leads match the current view.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 style={emptyTitleStyle}>No direct inquiries yet</h3>
      <p style={emptyBodyStyle}>
        When customers send inquiries from your shop page, they'll show up
        here. We'll also email you so you don't miss anything.
      </p>
      <ShareShopUrlButton shop={shop} />
    </Card>
  );
}

function PoolEmpty({ hasAnyPool, filterActive }) {
  if (filterActive) {
    return (
      <Card>
        <h3 style={emptyTitleStyle}>No matches</h3>
        <p style={emptyBodyStyle}>
          No pool requests match the current filter or search.
        </p>
      </Card>
    );
  }

  if (hasAnyPool) {
    return (
      <Card>
        <h3 style={emptyTitleStyle}>Nothing to show</h3>
        <p style={emptyBodyStyle}>
          You're all caught up — no pool requests match the current view.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 style={emptyTitleStyle}>The pool is quiet right now</h3>
      <p style={emptyBodyStyle}>
        When customers post open quote requests to Tattune, you'll see
        them here. They appear automatically — no setup needed.
      </p>
    </Card>
  );
}

function NoShopState({ onGoToWizard }) {
  return (
    <Card>
      <h3 style={emptyTitleStyle}>Set up your shop first</h3>
      <p style={emptyBodyStyle}>
        Your inbox starts filling up the moment your shop is live. Finish your
        artist profile to start receiving inquiries.
      </p>
      <button
        type="button"
        className="tt-inbox-btn-primary"
        onClick={onGoToWizard}
        style={primaryButtonStyle}
      >
        Open the artist wizard
      </button>
    </Card>
  );
}

function ArtistGate({ onUpgrade }) {
  return (
    <Card>
      <h3 style={emptyTitleStyle}>Inbox is an Artist Pro feature</h3>
      <p style={emptyBodyStyle}>
        The Tattune inbox lets you track every direct inquiry and pool
        request in one place — with status, search, and instant updates.
        It's part of Artist Pro, alongside your public profile, flash
        uploads, and lead handling.
      </p>
      <button
        type="button"
        className="tt-inbox-btn-primary"
        onClick={onUpgrade}
        style={primaryButtonStyle}
      >
        See Artist Pro
      </button>
    </Card>
  );
}

// SkeletonScaffold — placeholder layout shown during the initial load.
// Mirrors the real layout (tabs / chips / search / list rows) so the page
// doesn't visibly reflow when data arrives. Pure CSS pulse, no JS.
function SkeletonScaffold() {
  return (
    <>
      {/* Tabs */}
      <div
        aria-hidden="true"
        style={{
          display: "flex",
          gap: 6,
          padding: 4,
          background: "#0f0f0f",
          border: "1px solid #1f1f1f",
          borderRadius: 12,
          marginBottom: 18,
        }}
      >
        <SkeletonBlock height={36} flex={1} radius={9} />
        <SkeletonBlock height={36} flex={1} radius={9} />
      </div>

      {/* Filter chips */}
      <div
        aria-hidden="true"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: 12,
        }}
      >
        {[60, 78, 96, 78, 78, 90].map((w, i) => (
          <SkeletonBlock key={i} height={28} width={w} radius={999} />
        ))}
      </div>

      {/* Search */}
      <SkeletonBlock height={40} radius={10} style={{ marginBottom: 18 }} />

      {/* Rows */}
      <div
        style={{
          background: "#0f0f0f",
          border: "1px solid #1f1f1f",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <SkeletonRow key={i} first={i === 0} />
        ))}
      </div>
    </>
  );
}

function SkeletonRow({ first }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 12,
        padding: "14px 14px 14px 12px",
        borderTop: first ? "none" : "1px solid #1a1a1a",
      }}
    >
      <div style={{ width: 8 }} />
      <SkeletonBlock width={56} height={56} radius={8} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <SkeletonBlock width={140} height={14} />
          <SkeletonBlock width={56} height={16} radius={999} />
        </div>
        <SkeletonBlock width="62%" height={12} style={{ marginBottom: 6 }} />
        <SkeletonBlock width="86%" height={12} style={{ marginBottom: 6 }} />
        <SkeletonBlock width={70} height={10} />
      </div>
    </div>
  );
}

function SkeletonBlock({
  width,
  height,
  flex,
  radius = 6,
  style,
}) {
  return (
    <div
      className="tt-inbox-skeleton"
      style={{
        width,
        height,
        flex,
        borderRadius: radius,
        background: "#1a1a1a",
        ...style,
      }}
    />
  );
}

function ShareShopUrlButton({ shop }) {
  // Prefer the artist's public profile (/artists/<slug>) if we have a slug;
  // fall back to the directory landing if shop hasn't been set up enough
  // to have one yet (the empty-state still surfaces so artists know to
  // configure their shop).
  const slug = shop?.slug;
  const url = slug
    ? `${window.location.origin}/artists/${slug}`
    : `${window.location.origin}/`;

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(
        slug
          ? "Profile link copied — share it anywhere."
          : "Directory link copied — finish your shop to get a profile link."
      );
    } catch {
      toast.error("Couldn't copy — copy from the address bar instead.");
    }
  };

  return (
    <button
      type="button"
      className="tt-inbox-btn-ghost"
      onClick={handleClick}
      style={ghostButtonStyle}
      title={url}
    >
      {slug ? "Copy your profile link" : "Copy directory link"}
    </button>
  );
}

// ===========================================================================
// Layout shell + shared style atoms
// ===========================================================================

function PageShell({ children, onBack }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0a0a",
        color: "#eaeaea",
        padding: "32px 20px 80px",
      }}
    >
      <style>{inboxButtonStyles}</style>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "transparent",
            border: "1px solid #2a2a2a",
            color: "#d7d7d7",
            padding: "6px 12px",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          ← Back to app
        </button>

        <h1
          style={{
            fontSize: 24,
            margin: "0 0 6px",
            letterSpacing: "0.02em",
          }}
        >
          Inbox
        </h1>
        <p style={{ fontSize: 13, color: "#a8a8a8", margin: "0 0 24px" }}>
          Direct inquiries and pool requests for your shop.
        </p>

        {children}
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div
      style={{
        background: "#111111",
        border: "1px solid #1f1f1f",
        borderRadius: 14,
        padding: "24px 26px",
      }}
    >
      {children}
    </div>
  );
}

const emptyTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  margin: "0 0 8px",
  color: "#eaeaea",
};

const emptyBodyStyle = {
  fontSize: 14,
  color: "#a8a8a8",
  lineHeight: 1.55,
  margin: "0 0 16px",
};

const primaryButtonStyle = {
  padding: "11px 18px",
  border: "none",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  letterSpacing: "0.02em",
};

const ghostButtonStyle = {
  padding: "9px 14px",
  border: "1px solid #2a2a2a",
  borderRadius: 10,
  fontSize: 13,
  cursor: "pointer",
};

// ===========================================================================
// Helpers
// ===========================================================================

function truncate(s, max) {
  if (!s) return "";
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24 && sameDay(then, now)) {
    return `Today, ${then.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }
  if (isYesterday(then, now)) {
    return `Yesterday, ${then.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }
  // Older than yesterday: show month + day, plus year if it's not this year.
  const sameYear = then.getFullYear() === now.getFullYear();
  return then.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

function formatExpiry(iso) {
  if (!iso) return "";
  const exp = new Date(iso);
  if (Number.isNaN(exp.getTime())) return "";
  const now = new Date();
  const diffMs = exp.getTime() - now.getTime();
  if (diffMs <= 0) return "Expired";
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return "Expires tomorrow";
  return `Expires in ${diffDays}d`;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isYesterday(a, now) {
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  return sameDay(a, y);
}

// ---- filtering ------------------------------------------------------------

function filterDirect(direct, filter, search) {
  const q = search.trim().toLowerCase();
  return direct.filter((lead) => {
    if (!matchesDirectFilter(lead, filter)) return false;
    if (!q) return true;
    return (
      (lead.user_name || "").toLowerCase().includes(q) ||
      (lead.user_email || "").toLowerCase().includes(q) ||
      (lead.note || "").toLowerCase().includes(q)
    );
  });
}

function matchesDirectFilter(lead, filter) {
  switch (filter) {
    case "all":
      // Default view hides archived — same convention as email inboxes.
      return lead.status !== "archived";
    case "unread":
      return lead.status === "new";
    case "contacted":
      return lead.status === "contacted";
    case "booked":
      return lead.status === "booked";
    case "passed":
      return lead.status === "passed";
    case "archived":
      return lead.status === "archived";
    default:
      return true;
  }
}

function filterPool(pool, filter, search) {
  const q = search.trim().toLowerCase();
  return pool.filter(({ request, view }) => {
    if (!matchesPoolFilter(view, filter)) return false;
    if (!q) return true;
    const stylesJoined = Array.isArray(request.style_tags)
      ? request.style_tags.join(" ").toLowerCase()
      : "";
    return (
      (request.user_name || "").toLowerCase().includes(q) ||
      (request.user_email || "").toLowerCase().includes(q) ||
      (request.note || "").toLowerCase().includes(q) ||
      stylesJoined.includes(q)
    );
  });
}

// ---- sorting --------------------------------------------------------------
//
// "newest"  — created_at desc (matches the server-side default order, kept
//             explicit here so realtime reorders survive a re-render).
// "oldest"  — created_at asc.
// "unread"  — unread first (status='new' for direct, no view row for pool),
//             tiebroken by newest. Within the read group, also newest-first.

function sortDirect(rows, sort) {
  const copy = rows.slice();
  if (sort === "oldest") {
    copy.sort((a, b) => tsAsc(a.created_at, b.created_at));
    return copy;
  }
  if (sort === "unread") {
    copy.sort((a, b) => {
      const aUnread = a.status === "new" ? 0 : 1;
      const bUnread = b.status === "new" ? 0 : 1;
      if (aUnread !== bUnread) return aUnread - bUnread;
      return tsDesc(a.created_at, b.created_at);
    });
    return copy;
  }
  copy.sort((a, b) => tsDesc(a.created_at, b.created_at));
  return copy;
}

function sortPool(rows, sort) {
  const copy = rows.slice();
  if (sort === "oldest") {
    copy.sort((a, b) =>
      tsAsc(a.request.created_at, b.request.created_at)
    );
    return copy;
  }
  if (sort === "unread") {
    copy.sort((a, b) => {
      const aUnread = a.view ? 1 : 0;
      const bUnread = b.view ? 1 : 0;
      if (aUnread !== bUnread) return aUnread - bUnread;
      return tsDesc(a.request.created_at, b.request.created_at);
    });
    return copy;
  }
  copy.sort((a, b) =>
    tsDesc(a.request.created_at, b.request.created_at)
  );
  return copy;
}

function tsDesc(a, b) {
  return new Date(b || 0).getTime() - new Date(a || 0).getTime();
}

function tsAsc(a, b) {
  return new Date(a || 0).getTime() - new Date(b || 0).getTime();
}

function matchesPoolFilter(view, filter) {
  // Note the asymmetry with direct: pool statuses are a smaller set
  // (viewed / responded / passed / archived), and unread = no view row.
  switch (filter) {
    case "all":
      return view?.status !== "archived";
    case "unread":
      return !view;
    case "contacted":
      // "contacted" is the direct-side label; the pool equivalent is
      // "responded." Map them so the same chip works for both tabs.
      return view?.status === "responded";
    case "booked":
      // No "booked" state for pool requests (multiple shops competing).
      // Filter to nothing so the empty-state explains itself.
      return false;
    case "passed":
      return view?.status === "passed";
    case "archived":
      return view?.status === "archived";
    default:
      return true;
  }
}
